// CompteRenduChantierPage.jsx
// Page de création et gestion des comptes rendus de chantier
// Avec dictée vocale, photos avant/après, checklist et envoi client

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Camera, Mic, MicOff, Send, Save, Check, X, Plus,
  MapPin, Clock, FileText, Image, Trash2, ChevronDown, ChevronUp,
  Sparkles, Loader2, CheckCircle2, AlertCircle, Upload
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default checklist items
const DEFAULT_CHECKLIST = [
  { id: 'nettoyage', label: 'Nettoyage effectué', checked: false },
  { id: 'securite', label: 'Zone sécurisée', checked: false },
  { id: 'client_prevenu', label: 'Client prévenu', checked: false },
  { id: 'materiel_range', label: 'Matériel rangé', checked: false },
  { id: 'photos_prises', label: 'Photos prises', checked: false },
];

export default function CompteRenduChantierPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chantierId = searchParams.get('chantier_id');
  
  // State
  const [chantier, setChantier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Form data
  const [title, setTitle] = useState(`Compte rendu du ${new Date().toLocaleDateString('fr-FR')}`);
  const [description, setDescription] = useState('');
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST);
  const [photosBefore, setPhotosBefore] = useState([]);
  const [photosAfter, setPhotosAfter] = useState([]);
  const [sendToClient, setSendToClient] = useState(true);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Geolocation
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Photo sections
  const [showPhotosBefore, setShowPhotosBefore] = useState(true);
  const [showPhotosAfter, setShowPhotosAfter] = useState(true);
  
  const fileInputBeforeRef = useRef(null);
  const fileInputAfterRef = useRef(null);

  // Get token
  const getToken = () => {
    return localStorage.getItem('ecosystem_token') || 
           localStorage.getItem('buyer_token') || 
           localStorage.getItem('user_token');
  };

  // Load chantier data
  useEffect(() => {
    const loadChantier = async () => {
      if (!chantierId) {
        setLoading(false);
        return;
      }
      
      try {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/batiment/chantiers/${chantierId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setChantier(data);
          setTitle(`Compte rendu - ${data.client_name || 'Chantier'}`);
        }
      } catch (err) {
        console.error('Error loading chantier:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadChantier();
    getLocation();
  }, [chantierId]);

  // Get current location
  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          setLocationError('Géolocalisation non disponible');
        }
      );
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  // Transcribe audio using Whisper
  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const res = await fetch(`${API_URL}/api/ai/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setTranscription(data.text || '');
        // Auto-generate description from transcription
        if (data.text) {
          generateDescriptionFromAI(data.text);
        }
      }
    } catch (err) {
      console.error('Transcription error:', err);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Generate professional description using AI
  const generateDescriptionFromAI = async (text) => {
    setIsGeneratingAI(true);
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/compte-rendu/ai/generate-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          transcription: text,
          chantier_id: chantierId
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setDescription(data.generated || text);
      } else {
        setDescription(text);
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setDescription(text);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (event, type) => {
    const files = event.target.files;
    if (!files) return;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoData = {
          id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          data: e.target.result,
          name: file.name
        };
        
        if (type === 'before') {
          setPhotosBefore(prev => [...prev, photoData]);
        } else {
          setPhotosAfter(prev => [...prev, photoData]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove photo
  const removePhoto = (id, type) => {
    if (type === 'before') {
      setPhotosBefore(prev => prev.filter(p => p.id !== id));
    } else {
      setPhotosAfter(prev => prev.filter(p => p.id !== id));
    }
  };

  // Toggle checklist item
  const toggleChecklistItem = (id) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  // Add custom checklist item
  const addChecklistItem = () => {
    const label = prompt('Ajouter un élément à la checklist:');
    if (label) {
      setChecklist(prev => [...prev, {
        id: `custom_${Date.now()}`,
        label,
        checked: false
      }]);
    }
  };

  // Submit compte rendu
  const handleSubmit = async (asDraft = false) => {
    if (!description.trim()) {
      alert('Veuillez ajouter une description');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = getToken();
      
      const payload = {
        chantier_id: chantierId,
        title,
        description,
        checklist,
        photos_before: photosBefore.map(p => p.data),
        photos_after: photosAfter.map(p => p.data),
        latitude: location?.latitude,
        longitude: location?.longitude,
        send_to_client: !asDraft && sendToClient,
        voice_transcription: transcription || null
      };
      
      const res = await fetch(`${API_URL}/api/compte-rendu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        const error = await res.json();
        alert(error.detail || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-gray-900">Compte rendu de passage</h1>
            {chantier && (
              <p className="text-sm text-gray-500">{chantier.client_name}</p>
            )}
          </div>
          {location && (
            <div className="flex items-center gap-1 text-green-600 text-xs">
              <MapPin size={14} />
              <span>GPS</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titre du compte rendu
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="Ex: Pose carrelage salle de bain"
          />
        </div>

        {/* Voice Recording Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-indigo-600" />
            <span className="font-medium text-gray-800">Dictée vocale avec IA</span>
          </div>
          
          <p className="text-sm text-gray-600 mb-3">
            Parlez librement, l'IA rédigera un compte rendu professionnel
          </p>
          
          <div className="flex items-center gap-3">
            <motion.button
              onClick={isRecording ? stopRecording : startRecording}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium ${
                isRecording 
                  ? 'bg-red-500 text-white' 
                  : 'bg-indigo-600 text-white'
              }`}
              whileTap={{ scale: 0.95 }}
              disabled={isTranscribing || isGeneratingAI}
            >
              {isRecording ? (
                <>
                  <MicOff size={18} />
                  <span>Arrêter</span>
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                </>
              ) : (
                <>
                  <Mic size={18} />
                  <span>Dicter</span>
                </>
              )}
            </motion.button>
            
            {(isTranscribing || isGeneratingAI) && (
              <div className="flex items-center gap-2 text-sm text-indigo-600">
                <Loader2 size={16} className="animate-spin" />
                <span>{isTranscribing ? 'Transcription...' : 'Génération IA...'}</span>
              </div>
            )}
          </div>
          
          {transcription && (
            <div className="mt-3 p-2 bg-white/50 rounded-lg text-sm text-gray-600">
              <span className="text-xs text-gray-400">Transcription originale:</span>
              <p className="italic">"{transcription}"</p>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description des travaux
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="Décrivez les travaux effectués..."
          />
        </div>

        {/* Photos Before */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <button
            onClick={() => setShowPhotosBefore(!showPhotosBefore)}
            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <Camera size={18} className="text-gray-600" />
              <span className="font-medium">Photos AVANT ({photosBefore.length})</span>
            </div>
            {showPhotosBefore ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showPhotosBefore && (
            <div className="p-4">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {photosBefore.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={photo.data} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(photo.id, 'before')}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              
              <input
                ref={fileInputBeforeRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handlePhotoUpload(e, 'before')}
              />
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputBeforeRef.current?.click()}
              >
                <Plus size={16} className="mr-2" />
                Ajouter photo
              </Button>
            </div>
          )}
        </div>

        {/* Photos After */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <button
            onClick={() => setShowPhotosAfter(!showPhotosAfter)}
            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <Camera size={18} className="text-green-600" />
              <span className="font-medium">Photos APRÈS ({photosAfter.length})</span>
            </div>
            {showPhotosAfter ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showPhotosAfter && (
            <div className="p-4">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {photosAfter.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={photo.data} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(photo.id, 'after')}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              
              <input
                ref={fileInputAfterRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handlePhotoUpload(e, 'after')}
              />
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputAfterRef.current?.click()}
              >
                <Plus size={16} className="mr-2" />
                Ajouter photo
              </Button>
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Check size={18} className="text-green-600" />
              <span className="font-medium">Checklist de passage</span>
            </div>
            <button
              onClick={addChecklistItem}
              className="text-sm text-orange-600 hover:text-orange-700"
            >
              + Ajouter
            </button>
          </div>
          
          <div className="space-y-2">
            {checklist.map((item) => (
              <label
                key={item.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => toggleChecklistItem(item.id)}
                  className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className={item.checked ? 'text-gray-500 line-through' : 'text-gray-800'}>
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Send to client option */}
        <div className="bg-white rounded-xl border p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sendToClient}
              onChange={(e) => setSendToClient(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <div>
              <span className="font-medium text-gray-800">Envoyer au client</span>
              <p className="text-sm text-gray-500">Le client recevra une notification avec ce compte rendu</p>
            </div>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleSubmit(true)}
            disabled={saving}
          >
            <Save size={16} className="mr-2" />
            Brouillon
          </Button>
          
          <Button
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
            onClick={() => handleSubmit(false)}
            disabled={saving || !description.trim()}
          >
            {saving ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Send size={16} className="mr-2" />
            )}
            {sendToClient ? 'Envoyer' : 'Valider'}
          </Button>
        </div>
      </div>

      {/* Success overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compte rendu créé !</h3>
              <p className="text-gray-600">
                {sendToClient ? 'Le client a été notifié' : 'Enregistré en brouillon'}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
